import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/utils';
import { THEME_LABELS } from '@/lib/company-qualification';
import { QualificationReadView } from '@/components/profile/QualificationReadView';
import { QualificationThemeBlock } from '@/components/profile/QualificationThemeBlock';
import { QualificationProposalPanel } from '@/components/profile/QualificationProposalPanel';
import {
  type CompanyQualificationSheet,
  QUALIFICATION_THEMES,
  type QualificationThemeKey,
  SYNTHESIS_SECTIONS,
} from '@shared/types/company-qualification';

interface Props {
  sheet: CompanyQualificationSheet;
  focused: QualificationThemeKey | null;
  onFocus: (theme: QualificationThemeKey | null) => void;
  mode: 'read' | 'edit';
  onMode: (mode: 'read' | 'edit') => void;
  onChange: (sheet: CompanyQualificationSheet) => void;
  dirty: boolean;
  saving: boolean;
  analystRunning: boolean;
  error: string | null;
  notice: string | null;
  /** Resolves true once saved; the note is kept when the save fails. */
  onSave: (note: string | undefined) => Promise<boolean>;
  onReload: () => void;
  onDeepen: (theme: QualificationThemeKey) => void;
  onPage: (theme: QualificationThemeKey) => void;
}

const Chip = ({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) => (
  <button
    type="button"
    aria-pressed={active}
    onClick={onClick}
    className={cn('rounded-full border px-3 py-1 text-sm', active ? 'border-domino-aubergine bg-domino-lilac text-domino-aubergine' : 'border-domino-cardBorder text-domino-inkSoft hover:text-domino-ink')}
  >
    {children}
  </button>
);

/** The detailed sheet: every line, by theme, to read or to change by hand. */
export const QualificationSheetView = (props: Props) => {
  const { sheet, focused, mode, dirty, onChange } = props;
  const [note, setNote] = useState('');
  return (
    <div className="min-w-0 space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Thème affiché">
          <Chip active={!focused} onClick={() => props.onFocus(null)}>Tout</Chip>
          {QUALIFICATION_THEMES.map(({ key }) => (
            <Chip key={key} active={focused === key} onClick={() => props.onFocus(key)}>{THEME_LABELS[key].label}</Chip>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {focused && <Button size="sm" variant="secondary" onClick={() => props.onDeepen(focused)}>Approfondir avec l’analyste</Button>}
          <div className="flex rounded-lg border border-domino-cardBorder p-0.5 text-sm" role="group" aria-label="Affichage de la fiche">
            {(['read', 'edit'] as const).map((value) => (
              <button
                key={value}
                type="button"
                aria-pressed={mode === value}
                onClick={() => props.onMode(value)}
                className={cn('rounded-md px-3 py-1', mode === value ? 'bg-domino-lilac font-semibold text-domino-aubergine' : 'text-domino-inkSoft hover:text-domino-ink')}
              >
                {value === 'read' ? 'Lecture' : 'Modifier'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {mode === 'read' ? <QualificationReadView sheet={sheet} focused={focused} onPage={props.onPage} /> : (
        <>
          {QUALIFICATION_THEMES.filter(({ key }) => !focused || key === focused).map(({ key, sections }) => (
            <QualificationThemeBlock key={key} sheet={sheet} onChange={onChange} theme={key} sections={sections} />
          ))}
          {!focused && <QualificationThemeBlock sheet={sheet} onChange={onChange} theme={null} sections={SYNTHESIS_SECTIONS} />}
          {!focused && (
            <div className="rounded-xl border border-domino-cardBorder bg-domino-warm p-5">
              <QualificationProposalPanel sheet={sheet} onChange={onChange} />
            </div>
          )}
        </>
      )}

      {(mode === 'edit' || dirty) && (
        <div className="sticky bottom-0 flex flex-wrap items-center gap-3 border-t border-domino-cardBorder bg-domino-ivory/95 py-3 backdrop-blur">
          <Input value={note} onChange={(event) => setNote(event.target.value)} placeholder="Ce qui a changé (facultatif)" className="min-w-[12rem] flex-1" disabled={!dirty} />
          <Button onClick={() => void props.onSave(note.trim() || undefined).then((saved) => { if (saved) setNote(''); })} loading={props.saving} disabled={!dirty || props.analystRunning}>Enregistrer</Button>
          {props.analystRunning && <p className="w-full text-xs text-domino-inkSoft">L’analyste écrit dans la fiche : enregistrez une fois son tour terminé.</p>}
          {props.error && <p className="w-full text-sm text-red-700">{props.error} {props.error.includes('rechargez') && <button type="button" className="underline" onClick={props.onReload}>Recharger</button>}</p>}
          {props.notice && !props.error && <p className="w-full text-sm text-domino-inkSoft">{props.notice}</p>}
        </div>
      )}
      {mode === 'read' && !dirty && (props.error || props.notice) && <p className={cn('text-sm', props.error ? 'text-red-700' : 'text-domino-inkSoft')}>{props.error ?? props.notice}</p>}
    </div>
  );
};
